import { TextField } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import DefaultButton from "../../../../../styles/materialButtonStyle";
import axios from "axios";
import userAuthenticationConfig from "../../../../../../utils/userAuthenticationConfig";
import { check2FA } from "../../../../../../utils/check2FA";
import getUserInfo from "../../../../../../utils/getUserInfo";
import { HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../../../../utils/consts";

import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../../../../styles/styledDialog";
import { parseAxiosError } from "../../../../../../utils/response";
import ProjectsSelect from "../../../../../elements/projects/ProjectsSelect";
import ServiceFieldsInput from "../../../../../elements/services/ServiceFieldsInput";
import DialogConnectionsList from "./DialogConnectionsList";

const CreateConnectionDialog = ({
  t,
  setOpen,
  notification,
  setNotification,
  serviceId,
  userId,
  setConnections
}) => {

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [cancelDisabled, setCancelDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [serviceConnections, setServiceConnections] = useState([]);

  const defaultData = {
    service: serviceId,
    project: null,
    name: null,
    twofa: null,
    connectionFields: [],
    copyId: null
  };
  const [connectionData, setConnectionData] = useState(defaultData);

  const fetchConnections = () => {
    const filterData = "?service.id=" + serviceId + "&pagination=false";

    axios.get("/api/connections" + filterData, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setServiceConnections(response.data["hydra:member"]);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  const createConnection = async () => {
    setCancelDisabled(true);
    setSubmitDisabled(true);

    let check2Fa = await check2FA(getUserInfo()["userId"], connectionData.twofa);

    if (check2Fa.error) {
      setNotification({ ...notification, visible: true, type: "error", message: check2Fa.error });
      return;
    }

    for (let item in connectionData.connectionFields) {
      if (connectionData.connectionFields[item] instanceof File) {
        let formData = new FormData();
        formData.append("file", connectionData.connectionFields[item]);
        await axios.post("/api/media-objects", formData, { ...userAuthenticationConfig(false, true) }).
          then(response => {
            if (response.status === HTTP_STATUSES.HTTP_CREATED) {
              connectionData.connectionFields[item] = response.data.filePath;
            }
            setOpen(false);
          }).
          catch(error => {
            parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
          });
      }
    }

    let data = {
      service: serviceId,
      project: connectionData.project.id,
      connectionFields: connectionData.connectionFields,
      name: connectionData.name
    };

    axios.post("/api/connections", data, userAuthenticationConfig(false)).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {
        setNotification({ ...notification, visible: true, type: "success", message: t("details.addForm.success") });
        setConnections(prevState => [...prevState, response.data]);
      }

      setOpen(false);
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => {
      setCancelDisabled(false);
      setSubmitDisabled(false);
    });
  };

  const copyConnection = () => {
    setCancelDisabled(true);
    setSubmitDisabled(true);

    let data = {
      service: serviceId,
      project: connectionData.project.id,
      name: connectionData.name,
      twofa: connectionData.twofa
    };

    axios.post("/api/connections/" + connectionData.copyId + "/copy", data, userAuthenticationConfig(false)).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_CREATED) {
        setNotification({ ...notification, visible: true, type: "success", message: t("details.addForm.success") });
        setConnections(prevState => [...prevState, response.data]);
      }

      setOpen(false);
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => {
      setCancelDisabled(false);
      setSubmitDisabled(false);
    });
  };

  const handleClose = () => {
    setConnectionData(defaultData);
    setOpen(false);
  };

  const handleCopy = (id) => {
    setConnectionData(prevState => ({ ...prevState, copyId: id }));
  };

  const handleClearCopy = () => {
    setConnectionData(prevState => ({ ...prevState, copyId: null }));
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    if (connectionData.project) {
      setSubmitDisabled(false);
    }
  }, [connectionData]);

  return (
    <>
      <StyledDialogWrapper>
        <StyledDialogHead>
          {t("details.addForm.title")}
        </StyledDialogHead>
        <StyledDialogContent>
          <ProjectsSelect
            checkedProjects={connectionData.project}
            setCheckedProjects={(data) => setConnectionData({ ...connectionData, project: data })}
            isObject={true}
            userId={userId}
          />
          {connectionData.copyId ? <TextField
              id="copyId"
              label={t("details.addForm.copyId")}
              value={connectionData.copyId}
              disabled={true}
            /> :
            <ServiceFieldsInput
              serviceId={serviceId}
              connectionFields={connectionData.connectionFields}
              setCheckedFields={(data) => setConnectionData({ ...connectionData, connectionFields: data })}
            />
          }
          <TextField
            id="name"
            label={t("details.addForm.name")}
            value={connectionData.name}
            onChange={(data) => setConnectionData({ ...connectionData, name: data.target.value })}
          />
          <TextField
            id="twofa"
            label={t("details.addForm.twoFa")}
            value={connectionData.twofa}
            onChange={(data) => setConnectionData({ ...connectionData, twofa: data.target.value })}
          />
          <DialogConnectionsList
            t={t}
            connections={serviceConnections}
            handleCopy={handleCopy}
            handleClearCopy={handleClearCopy}
            copyId={connectionData.copyId}
          />
        </StyledDialogContent>
        <StyledDialogAction col="2">
          <DefaultButton
            onClick={handleClose}
            color="danger"
            disabled={cancelDisabled}
          >
            {t("details.addForm.cancel")}
          </DefaultButton>
          <DefaultButton
            color="success"
            onClick={connectionData.copyId ? copyConnection : createConnection}
            disabled={submitDisabled || Object.keys(errors).length > 0}
          >
            {t("details.addForm.create")}
          </DefaultButton>
        </StyledDialogAction>
      </StyledDialogWrapper>
    </>
  );
};

export default CreateConnectionDialog;